import { dayFitsRequest, slotsForEvent, type CapacityRequest, type CapacityRules } from '@/utils/capacityRules';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { format, startOfMonth, endOfMonth, addMonths } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';

interface StoreHours {
  dia: string;
  abertura: string | null;
  fechamento: string | null;
  isOpen: boolean;
  /** Quando true, o fornecedor atende 24h nesse dia (sem janela de horário). */
  allDay?: boolean;
  available_events?: number;
}

interface SpecificDateLimit {
  date: string;
  available_events: number;
}

interface AvailabilityData {
  openingDays: string[] | null;
  openingDaysDelivery: string[] | null;
  openingHours: { horarios_funcionamento: StoreHours[]; capacity_mode?: string } | null;
  deliveryHours: { horarios_funcionamento: StoreHours[] } | null;
  specificDatesLimits: SpecificDateLimit[] | null;
  availableEvents: number | null;
  /** Horário limite (HH:MM, Brasília) para encomendas no mesmo dia. */
  orderCutoffTime: string | null;
}

// Map date-fns pt-BR day names to the day names stored in Opening_days/horarios_funcionamento
const DAY_NAME_MAP: Record<string, string> = {
  'segunda-feira': 'Segunda-feira',
  'terça-feira': 'Terça-feira',
  'quarta-feira': 'Quarta-feira',
  'quinta-feira': 'Quinta-feira',
  'sexta-feira': 'Sexta-feira',
  'sábado': 'Sábado',
  'domingo': 'Domingo',
};

const normalizeDayName = (dayName: string): string => {
  const lower = dayName.toLowerCase();
  return DAY_NAME_MAP[lower] || dayName;
};

/**
 * `request` (tipo de evento e convidados do pedido) deixa a capacidade por dia considerar as regras de vagas do fornecedor
 * (ex.: Casamento ou mais de 100 convidados ocupa o dia todo). Sem ele, o pedido ocupa 1 vaga.
 */
export const useSupplierAvailability = (
  supplierId: string | null | undefined,
  menuType?: string | null,
  request?: CapacityRequest,
) => {
  const [data, setData] = useState<AvailabilityData>({
    openingDays: null,
    openingDaysDelivery: null,
    openingHours: null,
    deliveryHours: null,
    specificDatesLimits: null,
    availableEvents: null,
    orderCutoffTime: null,
  });
  const [loading, setLoading] = useState(false);
  // Só consideramos a disponibilidade pronta quando a configuração do fornecedor e a
  // capacidade (datas lotadas) já chegaram — antes disso o calendário mostraria todos os
  // dias liberados, sem a regra de entrega do fornecedor.
  const [configLoaded, setConfigLoaded] = useState(false);
  const [capacityLoaded, setCapacityLoaded] = useState(false);
  const [fullDates, setFullDates] = useState<Set<string>>(new Set());
  const [eventCounts, setEventCounts] = useState<Record<string, number>>({});
  const [slotsUsed, setSlotsUsed] = useState<Record<string, number>>({});
  const [capacityByDate, setCapacityByDate] = useState<Record<string, number | null>>({});

  // Fetch supplier config
  useEffect(() => {
    if (!supplierId) return;
    setConfigLoaded(false);
    setCapacityLoaded(false);

    const fetchData = async () => {
      setLoading(true);
      try {
        const { data: supplier, error } = await (supabase as any)
          .from('suppliers')
          .select('Opening_days, Opening_days_delivery, available_events_complete, delivery_hours, available_dates_events, available_events, order_cutoff_time, id')
          .eq('id', supplierId)
          .maybeSingle();

        if (error) {
          console.error('Error fetching supplier availability:', error);
          return;
        }

        if (supplier) {
          setData({
            openingDays: supplier.Opening_days || null,
            openingDaysDelivery: supplier.Opening_days_delivery || null,
            openingHours: supplier.available_events_complete || null,
            deliveryHours: supplier.delivery_hours || null,
            specificDatesLimits: supplier.available_dates_events || null,
            availableEvents: supplier.available_events ?? null,
            orderCutoffTime: supplier.order_cutoff_time || null,
          });
        }
      } catch (err) {
        console.error('Error fetching supplier availability:', err);
      } finally {
        setLoading(false);
        setConfigLoaded(true);
      }
    };

    fetchData();
  }, [supplierId]);

  // Fetch capacity (full dates) from edge function
  const fetchCapacity = useCallback(async (referenceDate?: Date) => {
    if (!supplierId) return;
    const ref = referenceDate || new Date();
    const start = format(startOfMonth(ref), 'yyyy-MM-dd');
    const end = format(endOfMonth(addMonths(ref, 36)), 'yyyy-MM-dd');

    try {
      const { data: result, error } = await supabase.functions.invoke('check-supplier-capacity', {
        body: { supplier_id: supplierId, start_date: start, end_date: end },
      });

      if (error) {
        console.error('Capacity check error:', error);
        return;
      }

      if (result?.fullDates) {
        setFullDates(new Set(result.fullDates));
      }
      if (result?.eventCounts) {
        setEventCounts(result.eventCounts);
      }
      if (result?.slotsUsed) setSlotsUsed(result.slotsUsed);
      if (result?.capacityByDate) setCapacityByDate(result.capacityByDate);
    } catch (err) {
      console.error('Capacity check error:', err);
    }
  }, [supplierId]);

  // Auto-fetch capacity on mount. A função de capacidade pode demorar (partida a frio);
  // depois de 5s liberamos o calendário mesmo assim, para nunca travar a loja.
  useEffect(() => {
    if (!supplierId) return;
    let cancelled = false;
    const safety = setTimeout(() => { if (!cancelled) setCapacityLoaded(true); }, 5000);
    fetchCapacity().finally(() => {
      if (!cancelled) setCapacityLoaded(true);
    });
    return () => {
      cancelled = true;
      clearTimeout(safety);
    };
  }, [fetchCapacity, supplierId]);

  /**
   * Check if a specific date is disabled (not selectable)
   * Returns true if the date should be DISABLED
   */
  const isDateDisabled = useCallback((date: Date): boolean => {
    // Always disable past dates
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date < today) return true;

    // Horário limite do fornecedor para encomendas: o pedido precisa ser feito até
    // o limite do DIA ANTERIOR à entrega. Ex.: limite 12:00 e entrega às quartas —
    // pedidos até 12h da terça habilitam a quarta seguinte; depois disso a data some
    // e vale a próxima data disponível (a quarta da semana seguinte).
    if (menuType === 'selecao' && data.orderCutoffTime && isBeyondOrderCutoff(date, data.orderCutoffTime)) {
      return true;
    }

    // Level 1: Check opening days (recurring weekly)
    const isSelectable = menuType === 'selecao';
    const activeOpeningDays = isSelectable 
      ? (data.openingDaysDelivery || ["Segunda-feira", "Terça-feira", "Quarta-feira", "Quinta-feira", "Sexta-feira", "Sábado"])
      : data.openingDays;

    if (activeOpeningDays && Array.isArray(activeOpeningDays) && activeOpeningDays.length > 0) {
      const dayName = normalizeDayName(format(date, 'EEEE', { locale: ptBR }));
      if (!activeOpeningDays.includes(dayName)) {
        return true;
      }
    }

    // Também valida o flag isOpen da grade de horários da modalidade correta:
    // encomendas (selecao) usam `delivery_hours`; eventos usam `available_events_complete`.
    const activeHoursSource = isSelectable
      ? data.deliveryHours?.horarios_funcionamento
      : data.openingHours?.horarios_funcionamento;

    if (activeHoursSource) {
      const dayName = normalizeDayName(format(date, 'EEEE', { locale: ptBR }));
      const dayConfig = activeHoursSource.find((d) => d.dia === dayName);
      if (dayConfig && dayConfig.isOpen === false) {
        return true;
      }
    }

    // Level 2: Check specific date exceptions (sold out)
    if (data.specificDatesLimits && Array.isArray(data.specificDatesLimits)) {
      const dateStr = format(date, 'yyyy-MM-dd');
      const specific = data.specificDatesLimits.find((d) => d.date === dateStr);
      if (specific && specific.available_events === 0) {
        return true;
      }
    }

    // Level 3: Check capacity vs actual event count (from edge function)
    const dateStr = format(date, 'yyyy-MM-dd');
    // Evento (não encomenda): vagas já ocupadas no dia + vagas que ESTE pedido ocupa (tipo e convidados) <= capacidade do dia.
    const used = slotsUsed[dateStr];
    if (menuType !== 'selecao' && used !== undefined) {
      const capacity = capacityByDate[dateStr] ?? null;
      const rules = ((data.openingHours as { capacity_rules?: CapacityRules } | null)?.capacity_rules) ?? null;
      const needed = slotsForEvent({ eventType: request?.eventType, guests: request?.guests }, rules, capacity);
      return !dayFitsRequest(used, capacity, needed);
    }
    if (fullDates.has(dateStr)) {
      return true;
    }

    return false;
  }, [data, fullDates, menuType, slotsUsed, capacityByDate, request?.eventType, request?.guests]);

  /**
   * Validate if a given time (and optional event duration) is within the supplier's operating hours for a given date.
   * If partyTimeDuration is provided (e.g. "04:00"), also checks that the event end doesn't exceed closing time.
   * Returns { valid: boolean; message?: string; abertura?: string; fechamento?: string }
   */
  const validateTime = useCallback((date: Date, time: string, partyTimeDuration?: string): { valid: boolean; message?: string; abertura?: string; fechamento?: string } => {
    // Use deliveryHours if menuType is 'selecao', otherwise use openingHours
    const isSelectable = menuType === 'selecao';
    const activeHoursSource = isSelectable 
      ? data.deliveryHours?.horarios_funcionamento 
      : data.openingHours?.horarios_funcionamento;

    if (!activeHoursSource || !time) {
      return { valid: true };
    }

    const dayName = normalizeDayName(format(date, 'EEEE', { locale: ptBR }));
    const dayConfig = activeHoursSource.find(
      (d) => d.dia === dayName
    );

    if (!dayConfig) {
      return { valid: true };
    }

    // Day explicitly closed for this modality
    if (dayConfig.isOpen === false) {
      return {
        valid: false,
        message: isSelectable
          ? 'Não realizamos entregas neste dia. Escolha outra data.'
          : 'Não atendemos eventos neste dia. Escolha outra data.',
      };
    }

    // Atendimento 24h: qualquer horário serve
    if (dayConfig.allDay === true) {
      return { valid: true };
    }

    if (dayConfig.abertura && dayConfig.fechamento) {
      const timeMinutes = timeToMinutes(time);
      const openMinutes = timeToMinutes(dayConfig.abertura);
      let closeMinutes = timeToMinutes(dayConfig.fechamento);

      // Janela que vira a madrugada (ex.: 18:00 até 02:00)
      const crossesMidnight = closeMinutes <= openMinutes;
      if (crossesMidnight) closeMinutes += 24 * 60;

      // Horário selecionado depois da meia-noite pertence ao fim da janela
      const normalizedTime = crossesMidnight && timeMinutes < openMinutes
        ? timeMinutes + 24 * 60
        : timeMinutes;

      if (normalizedTime < openMinutes || normalizedTime >= closeMinutes) {
        return {
          valid: false,
          message: `O horário de ${isSelectable ? 'entrega' : 'início do serviço'} deve ser entre ${dayConfig.abertura} e ${dayConfig.fechamento}.`,
          abertura: dayConfig.abertura,
          fechamento: dayConfig.fechamento,
        };
      }

      // Check if event end time exceeds closing time (only for events, not deliveries usually)
      if (partyTimeDuration && !isSelectable) {
        const durationParts = partyTimeDuration.split(':').map(Number);
        const durationMinutes = (durationParts[0] || 0) * 60 + (durationParts[1] || 0);
        const eventEndMinutes = normalizedTime + durationMinutes;

        if (eventEndMinutes > closeMinutes) {
          const maxStartMinutes = closeMinutes - durationMinutes;
          let maxH = Math.floor(maxStartMinutes / 60);
          const maxM = maxStartMinutes % 60;

          // Adjust for display if max time falls in the early morning
          if (maxH >= 24) maxH -= 24;

          return {
            valid: false,
            message: `Com a duração do evento, o serviço ultrapassaria o horário de atendimento (até ${dayConfig.fechamento}). Sugerimos iniciar até ${String(maxH).padStart(2, '0')}:${String(maxM).padStart(2, '0')}.`,
            abertura: dayConfig.abertura,
            fechamento: dayConfig.fechamento,
          };
        }
      }
    }

    return { valid: true, abertura: dayConfig.abertura || undefined, fechamento: dayConfig.fechamento || undefined };
  }, [data, menuType]);

  /**
   * Show toast and return false if date became unavailable
   */
  const validateDateOrWarn = useCallback((date: Date): boolean => {
    if (isDateDisabled(date)) {
      toast.error('Esta data não está disponível para este fornecedor. Por favor, escolha outra data.');
      return false;
    }
    return true;
  }, [isDateDisabled]);

  return {
    isDateDisabled,
    validateTime,
    validateDateOrWarn,
    availabilityData: data,
    loading,
    /** true quando a configuração e a capacidade do fornecedor já foram carregadas. */
    ready: !!supplierId && configLoaded && capacityLoaded,
    fetchCapacity,
    eventCounts,
  };
};

/** Data/hora atuais em Brasília (America/Sao_Paulo), independente do fuso do navegador. */
function brazilNowParts(): { dateStr: string; minutes: number } {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(new Date());
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '00';
  const hour = Number(get('hour')) % 24;
  return { dateStr: `${get('year')}-${get('month')}-${get('day')}`, minutes: hour * 60 + Number(get('minute')) };
}

/**
 * A entrega em `date` já perdeu o prazo? O pedido precisa ser feito até o horário
 * limite (HH:MM, Brasília) do dia anterior à entrega:
 *  - entrega hoje (ou antes): prazo já passou;
 *  - entrega amanhã: só se ainda estamos antes do limite de hoje;
 *  - entrega daqui a 2+ dias: dentro do prazo.
 */
function isBeyondOrderCutoff(date: Date, cutoff: string): boolean {
  const m = /^(\d{1,2}):(\d{2})$/.exec(cutoff.trim());
  if (!m) return false;
  const now = brazilNowParts();
  const toDayNumber = (iso: string) => {
    const [y, mo, d] = iso.split('-').map(Number);
    return Math.floor(Date.UTC(y, mo - 1, d) / 86_400_000);
  };
  const daysAhead = toDayNumber(format(date, 'yyyy-MM-dd')) - toDayNumber(now.dateStr);
  if (daysAhead <= 0) return true;
  if (daysAhead === 1) return now.minutes >= Number(m[1]) * 60 + Number(m[2]);
  return false;
}

function timeToMinutes(time: string): number {
  const parts = time.split(':').map(Number);
  const hour = parts[0] || 0;
  const minutes = parts[1] || 0;
  
  // Se for madrugada (ex: 01:00, 05:00), tratamos como sendo após as 24h para comparação linear
  // se o horário for até as 6h da manhã.
  return (hour <= 6 ? (hour + 24) : hour) * 60 + minutes;
}
